import { Box, Checkbox, Panel, PanelSection, Radio, Text, TextLabel } from '@customerio/pluma-components/react';
import { useWatch } from '@/components/form';
import type { ReportingWebhookFormApi } from '../types';

type WebhookOptionsFieldProps = {
  form: ReportingWebhookFormApi;
  isDisabled: boolean;
};

/**
 * Delivery options: how often an event is sent, and whether the message body travels with it.
 *
 * `resolution` reads backwards from the labels — it is false for "only the first time" and true for
 * "every time", so the two radios are not in the order the boolean suggests.
 */
export function WebhookOptionsField({ form, isDisabled }: WebhookOptionsFieldProps) {
  const resolution = useWatch({ control: form.control, name: 'resolution' });
  const withContent = useWatch({ control: form.control, name: 'with_content' });

  const setResolution = (value: boolean) => {
    form.setValue('resolution', value, { shouldDirty: true });
  };

  return (
    <Box as="fieldset" flexShrink="0">
      <Text as="legend" text="product-h4" mb="150">
        Options
      </Text>
      <Panel backgroundColor="subtle">
        <PanelSection>
          <Box as="fieldset" mb="300">
            <TextLabel as="legend" mb="150">
              Send Frequency
            </TextLabel>
            <Radio
              id="resolution_first_time"
              name="resolution"
              label="Send only the first time the event occurs"
              isChecked={resolution === false}
              isDisabled={isDisabled}
              onCheckedChange={() => setResolution(false)}
            />
            <Radio
              id="resolution_every_time"
              name="resolution"
              label="Send every time the event occurs"
              isChecked={resolution === true}
              isDisabled={isDisabled}
              onCheckedChange={() => setResolution(true)}
            />
          </Box>

          <Box as="fieldset">
            <TextLabel as="legend" mb="150">
              Body Content
            </TextLabel>
            <Checkbox
              data-test-with-content-toggle
              name="include_body_content"
              label="Include body content and headers in all Sent events"
              isChecked={withContent}
              isDisabled={isDisabled}
              onCheckedChange={(checked) => {
                form.setValue('with_content', checked, { shouldDirty: true });
              }}
            />
          </Box>
        </PanelSection>
      </Panel>
    </Box>
  );
}
